import { useState, useEffect } from 'react';
import { smsTemplatesDB, type SmsTemplate } from '../../lib/smsTemplatesDB';

type Props = {
  patientName: string;
  initialPhone?: string;
  onClose: () => void;
};

type SendState = 'idle' | 'sending' | 'success' | 'error';

export default function SendMessageModal({ patientName, initialPhone, onClose }: Props) {
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);

  const [phone, setPhone] = useState(initialPhone || '');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [message, setMessage] = useState('');
  const [sendState, setSendState] = useState<SendState>('idle');

  useEffect(() => {
    const tList = smsTemplatesDB.getAll();
    setTemplates(tList);
    if (tList.length > 0) {
      setSelectedTemplateId(tList[0].id);
      setMessage(tList[0].message);
    }
  }, []);

  const handleTemplateChange = (id: string) => {
    setSelectedTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) setMessage(t.message);
  };

  // Normalize phone: strip formatting, ensure no leading +
  const normalizePhone = (raw: string): string => {
    let p = raw.replace(/[\s\-().+]/g, '');
    if (p.startsWith('0')) p = '90' + p.slice(1);
    if (!p.startsWith('90')) p = '90' + p;
    return p;
  };

  const canSend = phone.trim().length >= 7 && message.trim().length > 0;

  // ── WhatsApp (unchanged) ─────────────────────────────────────────────────
  const handleWhatsApp = () => {
    const p = normalizePhone(phone);
    window.open(`https://wa.me/${p}?text=${encodeURIComponent(message)}`, '_blank');
    setSendState('success');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />

      <div className="relative bg-white rounded-2xl w-full max-w-md flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-cyan-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-white font-bold text-base">Mesaj Gönder</h2>
            <p className="text-teal-100 text-xs mt-0.5 truncate max-w-[260px]">{patientName}</p>
          </div>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 text-white transition-all cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">

          {/* Recipient phone */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Alıcı Telefon <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <i className="ri-phone-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="05XX XXX XX XX" autoFocus
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all" />
            </div>
          </div>

          {/* Template selector */}
          {templates.length > 0 ? (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Şablon</label>
              <select value={selectedTemplateId} onChange={(e) => handleTemplateChange(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all cursor-pointer bg-white">
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          ) : (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700 flex items-center gap-2">
              <i className="ri-information-line text-base"></i>
              Şablon eklenmemiş. Araçlar → Mesaj Şablonları sekmesinden ekleyin.
            </div>
          )}

          {/* Message */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-semibold text-gray-700">Mesaj</label>
              <span className="text-xs text-gray-400">{message.length} karakter</span>
            </div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
              placeholder="Mesaj içeriği..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all resize-none" />
          </div>

          {/* Status feedback */}
          {sendState === 'success' && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
              <i className="ri-check-circle-line text-base"></i>
              WhatsApp açılıyor...
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2">
          <button type="button" onClick={handleWhatsApp} disabled={!canSend}
            className={`w-full py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
              canSend ? 'bg-green-500 text-white hover:bg-green-600 cursor-pointer' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}>
            <i className="ri-whatsapp-line text-lg"></i>WhatsApp ile Gönder
          </button>
        </div>
      </div>
    </div>
  );
}
